import { Stem, StemGraphicGapMatch } from "@examspark/types";

import { scoreSingleGap } from "./text-inline";
import { getNewScore } from "./utils";

export const scoreGraphicGapMatch = (
  expected: StemGraphicGapMatch,
  actual?: Partial<Stem.Response>,
) => {
  const { scoring } = expected;
  const score = getNewScore();
  score.max = 0;

  if (!scoring) return score;

  const scoredSpots = scoreGraphicGapBySpot({
    expected,
    response: actual,
    scoring,
  });

  score.max = Object.values(scoredSpots).length;
  score.actual = Object.values(scoredSpots).filter(Boolean).length;

  return score;
};

export const scoreGraphicGapBySpot = ({
  expected,
  response,
  scoring,
}: {
  expected: StemGraphicGapMatch;
  response: Partial<Stem.Response> | undefined;
  scoring: Partial<Stem.Scoring> | undefined;
}): Record<string, boolean> => {
  const { content } = expected;

  if (!scoring) return {};
  // only score spots that have at least one correct answer
  const scorables =
    content.spots?.filter(
      (spot) =>
        (spot.shape === "text_gap" && textGapHasCorrectAnswer(spot, scoring)) ||
        (["rect", "circle", "polygon"].includes(spot.shape) &&
          droppableHasCorrectAnswer(spot, scoring)),
    ) ?? [];

  if (!response) {
    return Object.fromEntries(
      scorables.map((spot) => [spot.id, false] as const),
    );
  }

  const scoredSpots = scorables.map((spot) => {
    if (spot.shape === "text_gap") {
      const gap = content.gaps?.[spot.id];
      if (!gap) return [spot.id, false] as const;

      return [
        spot.id,
        !!scoreSingleGap(
          gap,
          spot.id,
          response.gaps ?? {},
          expected,
          scoring?.gaps,
        ),
      ] as const;
    }

    if (["rect", "circle", "polygon"].includes(spot.shape)) {
      const OptionsOrTokens = [
        ...(content.options ?? []),
        ...(content.tokens ?? []),
      ];

      const dropsInSpot = response?.drops
        ?.filter((drop) => drop.droppableId === spot.id)
        .map((drop) => ({
          drop: drop,
          item: OptionsOrTokens.find(
            (option) => option.id === drop.draggableId,
          ),
        }))
        .filter((drop): drop is DropValue => !!drop.item);

      const pairsForSpot = scoring.pairs?.filter(
        (pair) => pair.dst === spot.id,
      );

      if (!dropsInSpot || !pairsForSpot) return [spot.id, false] as const;

      return [
        spot.id,
        scoreDrop(
          scoring.gapMatchScoringMethod,
          dropsInSpot,
          pairsForSpot,
          spot.allowMultipleTokens,
        ),
      ] as const;
    }

    return [spot.id, false] as const;
  });

  return Object.fromEntries(scoredSpots);
};

export const textGapHasCorrectAnswer = (
  spot: Stem.Spot,
  scoring: Partial<Stem.Scoring>,
) => {
  if (spot.shape !== "text_gap") return false;

  const gap = scoring.gaps?.[spot.id];

  if (!gap) return false;

  return Object.values(gap.options).some((option) => option.isKey);
};

export const droppableHasCorrectAnswer = (
  spot: Stem.Spot,
  scoring: Partial<Stem.Scoring>,
) => {
  if (spot.shape === "text_gap") return false;

  return scoring.pairs
    ?.filter((pair) => pair.dst === spot.id)
    .some((pair) => pair.isKey);
};

type DropValue = {
  drop: Stem.Drop;
  item: Stem.Option | Stem.Token;
};

export const scoreDrop = (
  scoringType: Stem.Scoring["gapMatchScoringMethod"] | undefined,
  dropsInGap: DropValue[],
  pairsForGap: Array<Stem.Pair>,
  gapAcceptsMultipleTokens: boolean | undefined,
) => {
  if (!scoringType || scoringType === "TOKEN_PLACEMENT") {
    return scorePlacementDrop(
      dropsInGap,
      pairsForGap,
      !!gapAcceptsMultipleTokens,
    );
  } else if (scoringType === "GAP_VALUE") {
    return scoreValueDrop(dropsInGap, pairsForGap);
  }

  return false;
};

const scoreValueDrop = (
  dropsInGap: DropValue[],
  pairsForGap: Array<Stem.Pair>,
) => {
  return pairsForGap.every((pair) => {
    const dropSum = dropsInGap.reduce((acc, drop) => {
      return acc + (drop.item.numericValue ?? 0);
    }, 0);

    return pair.gapValue === dropSum;
  });
};

const scorePlacementDrop = (
  dropsInGap: DropValue[],
  pairsForGap: Array<Stem.Pair>,
  gapAcceptsMultipleTokens: boolean,
) => {
  const pairsLeftToScore = [...pairsForGap].filter((pair) => pair?.isKey);

  // if the gap accepts multiple tokens, then we assume that ALL relevant pairs must
  // be present
  if (gapAcceptsMultipleTokens) {
    return (
      dropsInGap.every((drop) => {
        const pair = pairsLeftToScore.find(
          (p) => p.src === drop.drop.draggableId,
        );

        if (pair) {
          pairsLeftToScore.splice(pairsLeftToScore.indexOf(pair), 1);
        }

        return pair;
      }) && pairsLeftToScore.length === 0
    );
    // if the gap only accepts one token, then we assume that only ONE relevant pair
    // must be present
  } else {
    return pairsForGap.some((pair) => {
      const drop = dropsInGap.find(
        (drop) => drop.drop.draggableId === pair.src,
      );

      return drop;
    });
  }
};
